import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, BookOpen, Users, GraduationCap, Building2,
  ScrollText, FlaskConical, ShieldCheck, Lightbulb, BadgeCheck,
  Database, FileSearch, ChevronRight
} from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/tracker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import PaperCard from "@/components/PaperCard";
import KineticHexBackdrop from "@/components/KineticHexBackdrop";
import CourseToPaperRoadmap from "@/components/CourseToPaperRoadmap";
import JourneyToResearcher from "@/components/JourneyToResearcher";
import AnimatedSlogan from "@/components/AnimatedSlogan";
import FloatingShapes from "@/components/FloatingShapes";
import useMouseParallax from "@/hooks/useMouseParallax";
import { getSlot } from "@/lib/brandAssets";
import { NotForProfitBanner, ResearchAreaCards, MentorHighlights, ImageCarousel, LatestNewsStrip } from "@/components/LandingExtras";

const SERVICES = [
  { icon: ScrollText, title: "Publication Consultation", desc: "End-to-end guidance — from idea framing to journal targeting and acceptance.", tag: "01" },
  { icon: GraduationCap, title: "Thesis Mentorship", desc: "1:1 mentorship for Bachelors, Masters, MPhil and PhD candidates across domains.", tag: "02" },
  { icon: FileSearch, title: "Academic Writing Support", desc: "Manuscript drafting, structural editing, language polishing, and submission packs.", tag: "03" },
  { icon: FlaskConical, title: "AI & Digital Research Training", desc: "Hands-on workshops on AI tools, data methods, and computational research.", tag: "04" },
  { icon: Building2, title: "Institutional Collaboration", desc: "Build research capacity in universities, colleges and research bodies.", tag: "05" },
  { icon: Users, title: "Workshops & Bootcamps", desc: "Cohort-based intensive programs for departments, faculties and research groups.", tag: "06" },
  { icon: Database, title: "Research Ecosystem", desc: "Connect researchers, mentors, journals, datasets and grants in one workspace.", tag: "07" },
  { icon: ShieldCheck, title: "Ethics & Integrity", desc: "Plagiarism checks, COI guidance, and ethical-AI usage protocols.", tag: "08" },
];

const PILLARS_LABELS = [
  { key: "ideas",        label: "Ideas Processed",   suffix: "+" },
  { key: "publications", label: "Publications",      suffix: "+" },
  { key: "mentors",      label: "Mentors Network",   suffix: "+" },
  { key: "universities", label: "Institutions",      suffix: "+" },
];

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [papers, setPapers] = useState([]);
  const [suggestedIdeas, setSuggestedIdeas] = useState([]);
  const heroRef = useMouseParallax();

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
    api.get("/papers/featured", { params: { limit: 6 } }).then((r) => setPapers(r.data)).catch(() => {});
    api.get("/research-ideas", { params: { limit: 6 } }).then((r) => setSuggestedIdeas(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="landing-page">
      <SEO
        title="Research Services in Nepal · Paper Help & Thesis Guidance"
        description="KhojByte Research Lab — a not-for-profit research consultancy in Nepal. Research services in Nepal, paper help, thesis-to-paper conversion, mentor matching, journal targeting and plagiarism checks."
        path="/"
        keywords="Research Services in Nepal, Research help in Nepal, Paper help in Nepal, Thesis mentorship Nepal, Publication consultancy Kathmandu, Academic writing Nepal, PhD support Nepal"
      />
      <Navbar />
      <NotForProfitBanner />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-white">
        <KineticHexBackdrop />
        <div
          className="absolute top-0 right-0 w-[50%] h-full hidden lg:block kb-parallax"
          style={{
            backgroundImage: `linear-gradient(to right, #ffffff 5%, transparent 60%), url(${getSlot("hero.primary")})`,
            backgroundSize: "cover",
            backgroundPosition: "right center",
            opacity: 0.55,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 lg:pt-32 lg:pb-36">
          <div className="max-w-3xl">
            <div className="kb-overline flex items-center gap-3 mb-7" data-testid="hero-overline">
              <span className="w-6 h-px bg-kb-ink-muted/40" />
              EST. KATHMANDU — RESEARCH × INNOVATION × ETHICS
            </div>
            <AnimatedSlogan />
            <p className="kb-slogan text-base mt-4">From an idea to a published, indexed, citable paper.</p>
            <p className="mt-8 text-lg text-kb-ink-muted leading-relaxed max-w-2xl">
              KhojByte Research Lab is Nepal's research publication and innovation studio —
              helping students, scholars and institutions transform raw ideas into rigorously
              reviewed, ethically grounded, globally presentable research.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register?intent=submit-idea&next=/dashboard/ideas/new" onClick={() => track("cta_submit_idea", { place: "hero" })} className="kb-btn-primary" data-testid="hero-cta-submit-idea">
                <Lightbulb className="w-4 h-4" /> Submit your research idea
              </Link>
              <Link to="/register?intent=guidance&next=/dashboard/guidance/new" onClick={() => track("cta_request_guidance", { place: "hero" })} className="kb-btn-primary bg-kb-ink hover:bg-kb-ink-muted text-white" data-testid="hero-cta-request-guidance">
                <Users className="w-4 h-4" /> Request mentor guidance
              </Link>
              <Link to="/register?intent=convert-thesis&next=/dashboard/ideas/new?source=thesis" onClick={() => track("cta_convert_thesis", { place: "hero" })} className="kb-btn-primary bg-white text-kb-ink border border-kb-ink hover:bg-kb-subtle" data-testid="hero-cta-convert-thesis">
                <ScrollText className="w-4 h-4" /> Convert your project / thesis
              </Link>
              <Link to="/services" className="kb-btn-outline" data-testid="hero-cta-secondary">
                Explore all services
              </Link>
            </div>

            {/* Quick services preview — at-a-glance pills */}
            <div className="mt-8 flex flex-wrap gap-2" data-testid="quick-services-strip">
              {[
                { to: "/services", l: "Publication" },
                { to: "/services", l: "Mentorship" },
                { to: "/research-ideas", l: "Research Ideas", premium: true },
                { to: "/explainers", l: "Paper Explainers", premium: true },
                { to: "/datasets", l: "Datasets" },
                { to: "/events", l: "Webinars" },
                { to: "/discussions", l: "Discussions" },
                { to: "/dashboard/journals", l: "Journal DB" },
              ].map((s) => (
                <Link key={s.l} to={s.to} className={`kb-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-sm border ${s.premium ? "border-kb-primary/40 bg-kb-primary/5 text-kb-primary" : "border-slate-200 bg-white text-kb-ink-muted hover:text-kb-primary"}`}>
                  {s.premium && <Sparkles className="inline w-3 h-3 -mt-0.5 mr-1" />}{s.l}
                </Link>
              ))}
            </div>
          </div>

          {/* Data ribbon — real DB counts (admin floors applied) */}
          <div className="mt-20 lg:mt-28 grid grid-cols-2 lg:grid-cols-4 gap-px bg-kb-divider border border-kb-divider rounded-sm overflow-hidden">
            {PILLARS_LABELS.map((p, i) => {
              const v = stats?.[p.key];
              const display = (v ?? "—").toLocaleString?.() ?? "—";
              return (
                <div key={p.key} className="bg-white p-6 lg:p-8">
                  <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">{`0${i+1}`}</div>
                  <div className="font-mono text-3xl lg:text-4xl text-kb-purple mt-2" data-testid={`pillar-${p.key}`}>{display}{v ? p.suffix : ""}</div>
                  <div className="text-sm text-kb-ink-muted mt-1">{p.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* JOURNEY TO BECOME A RESEARCHER */}
      <JourneyToResearcher />

      {/* RESEARCH DOMAINS — hierarchical drill-down (moved up, per request) */}
      <ResearchAreaCards />

      {/* PLATFORM PROMISE BAND */}
      <section className="bg-kb-dark text-white relative overflow-hidden">
        <div aria-hidden className="absolute -left-32 top-10 w-[420px] h-[420px] opacity-[0.12]"
          style={{ background: "radial-gradient(closest-side, #7A44FC, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-white/60 mb-4">
              THE PLATFORM
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-white">
              <span className="text-white/60 italic">A research operating system,</span><br />
              not just another consultancy.
            </h2>
          </div>
          <div className="lg:col-span-5 text-white/75 text-base leading-relaxed">
            <p>
              Submit an idea, get a feasibility read, match with a domain mentor, track every
              publication stage in real time, browse Q1-targeted journals with quartile data,
              run ethical plagiarism checks, and showcase your portfolio — all in one workspace.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">
              WHAT WE OFFER
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-kb-ink leading-tight">
              Eight services that move<br /> ideas <em className="italic text-kb-primary">to publication</em>.
            </h2>
          </div>
          <div className="lg:col-span-5 text-kb-ink-muted">
            Tightly integrated, opinionated, evidence-led. Each service is supported by tools
            inside our researcher dashboard.
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden stagger-in">
          {SERVICES.map((s) => (
            <div key={s.title} className="bg-white p-7 group transition-colors hover:bg-kb-subtle" data-testid={`service-${s.tag}`}>
              <div className="flex items-start justify-between">
                <s.icon className="w-6 h-6 text-kb-primary" strokeWidth={1.5} />
                <span className="kb-mono text-[10px] text-kb-ink-light">{s.tag}</span>
              </div>
              <div className="font-serif text-xl text-kb-ink mt-6 leading-tight">{s.title}</div>
              <div className="text-sm text-kb-ink-muted mt-2 leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LMS HIGHLIGHT BAND */}
      <section className="bg-gradient-to-br from-kb-primary/5 via-kb-bg to-kb-primary/10 border-y border-slate-200" data-testid="landing-lms-band">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary mb-4 inline-flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> LEARNING MANAGEMENT SYSTEM
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-kb-ink leading-tight">
              Learn <em className="italic text-kb-primary">how to publish</em> a research paper —<br />
              from your first abstract to acceptance.
            </h2>
            <p className="mt-5 text-kb-ink-muted max-w-2xl">
              Self-paced cohorts on academic writing, methodology, journal targeting, ethical AI usage,
              statistics & data viz, and survival skills for graduate research. Interactive lessons,
              quizzes, assignments and threaded peer discussions — built by senior researchers.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/dashboard/courses" className="kb-btn-primary" data-testid="lms-cta-primary">
                <GraduationCap className="w-4 h-4" /> Learn how to publish a paper
              </Link>
              <Link to="/dashboard/courses" className="kb-btn-outline" data-testid="lms-cta-browse">
                Browse all courses <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3" data-testid="lms-feature-grid">
            {[
              { i: GraduationCap, t: "Cohort-based", d: "Learn with peers, get mentor feedback" },
              { i: BadgeCheck, t: "Certified", d: "Earn shareable certificates on completion" },
              { i: FileSearch, t: "Hands-on", d: "Real assignments + peer review" },
              { i: BookOpen, t: "Curated", d: "Built by Q1-published researchers" },
            ].map((c) => (
              <div key={c.t} className="bg-white border border-slate-200 rounded-sm p-4">
                <c.i className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                <div className="font-serif text-base text-kb-ink mt-3">{c.t}</div>
                <div className="text-xs text-kb-ink-muted mt-1">{c.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORKFLOW SHOWCASE */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">
              WORKFLOW
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl text-kb-ink leading-tight">
              A live dashboard for every<br />stage of publication.
            </h2>
            <p className="mt-6 text-kb-ink-muted">
              From the moment you submit your idea to the day your paper is published — every
              checkpoint is visible, auditable and time-stamped.
            </p>
            <Link to="/register" className="kb-btn-primary mt-8" data-testid="workflow-cta">
              See your timeline <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="border border-slate-200 rounded-sm p-6 lg:p-8 bg-kb-bg">
              <div className="kb-mono text-[10px] text-kb-ink-light mb-4">PUBLICATION WORKFLOW</div>
              <div className="space-y-1">
                {[
                  { s: "Submitted", note: "Idea received • Auto feasibility scored" },
                  { s: "Feasibility Review", note: "Lab analyst review — 72h SLA" },
                  { s: "Mentor Matched", note: "Domain expert assigned" },
                  { s: "Drafting", note: "Structured writing sprints" },
                  { s: "Internal Review", note: "Peer + language review" },
                  { s: "Submitted to Journal", note: "Cover letter + targeting" },
                  { s: "Under Review", note: "Reviewer comments tracked" },
                  { s: "Accepted", note: "Acceptance handled & celebrated" },
                  { s: "Published", note: "Indexed in target databases" },
                ].map((row, i) => (
                  <div key={row.s} className="flex items-center gap-4 py-2.5 border-b border-slate-200/60 last:border-0">
                    <div className={`kb-mono text-[11px] w-8 ${i < 3 ? "text-emerald-600" : i === 3 ? "text-kb-primary" : "text-kb-ink-light"}`}>
                      {String(i+1).padStart(2,'0')}
                    </div>
                    <div className={`w-2 h-2 rounded-full ${i < 3 ? "bg-emerald-500" : i === 3 ? "bg-kb-primary ring-4 ring-kb-primary/15" : "bg-slate-300"}`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${i <= 3 ? "text-kb-ink font-medium" : "text-kb-ink-muted"}`}>{row.s}</div>
                      <div className="text-xs text-kb-ink-light">{row.note}</div>
                    </div>
                    {i === 3 && <span className="kb-mono text-[10px] uppercase text-kb-primary bg-kb-primary/10 px-2 py-1 rounded-sm">In Progress</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COURSE → PAPER ROADMAP */}
      <CourseToPaperRoadmap />

      {/* TOOLS / FEATURES STRIP */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">
          INSIDE THE DASHBOARD
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-kb-ink leading-tight max-w-3xl">
          Built for the precision of <em className="italic text-kb-primary">modern research</em>.
        </h2>

        <div className="mt-14 grid lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
          {[
            { icon: Lightbulb, t: "Idea Intake Portal", d: "Submit abstracts, datasets and methodology. Receive instant feasibility scoring." },
            { icon: Users, t: "Mentor Matching", d: "Routed to domain experts based on keywords, quartile target and methodology." },
            { icon: BookOpen, t: "Journal & Conference DB", d: "Searchable database with Q1-Q4 metrics, APC info and submission alerts." },
            { icon: GraduationCap, t: "Learning Management", d: "AI-literacy, writing, methods and ethics courses for researchers." },
            { icon: ShieldCheck, t: "Plagiarism Check", d: "Pre-submission ethical similarity check with explainable matches." },
            { icon: BadgeCheck, t: "Researcher Portfolio", d: "Showcase publications, certifications and ORCID — your academic homepage." },
          ].map((f, i) => (
            <div key={f.t} className="bg-white p-7" data-testid={`feature-${i}`}>
              <f.icon className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
              <div className="font-serif text-lg text-kb-ink mt-5">{f.t}</div>
              <div className="text-sm text-kb-ink-muted mt-2 leading-relaxed">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SUGGESTED RESEARCH IDEAS (curated to spark interest) */}
      {suggestedIdeas.length > 0 && (
        <section className="bg-gradient-to-b from-kb-subtle to-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
              <div className="lg:col-span-7">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">
                  SUGGESTED RESEARCH AREAS · CURATED
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-kb-ink leading-tight">
                  Looking for a <em className="italic text-kb-primary">research direction?</em>
                </h2>
                <p className="mt-4 text-kb-ink-muted max-w-xl">
                  KhojByte mentors curated these high-impact, ready-to-execute research ideas across domains —
                  perfect for thesis projects, papers, and grant proposals.
                </p>
              </div>
              <div className="lg:col-span-5 flex lg:justify-end">
                <Link to="/research-ideas" className="kb-btn-primary" data-testid="landing-ideas-cta">
                  Browse all ideas <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger-in">
              {suggestedIdeas.slice(0, 6).map((idea) => (
                <Link
                  key={idea.id}
                  to={`/research-ideas/${idea.id}`}
                  onClick={() => track("cta_suggested_idea", { idea_id: idea.id, place: "landing" })}
                  className="kb-card p-5 group hover:shadow-md transition-all flex flex-col"
                  data-testid={`landing-idea-${idea.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <Lightbulb className="w-5 h-5 text-kb-primary shrink-0" strokeWidth={1.5} />
                    <div className="flex gap-1">
                      {idea.is_premium && (
                        <span className="kb-mono text-[9px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">PREMIUM</span>
                      )}
                      {idea.difficulty && (
                        <span className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary bg-kb-primary/5 border border-kb-primary/15 px-1.5 py-0.5 rounded-sm">{idea.difficulty}</span>
                      )}
                    </div>
                  </div>
                  <h3 className="font-serif text-lg text-kb-ink mt-3 leading-snug line-clamp-2 group-hover:text-kb-primary transition-colors">
                    {idea.title}
                  </h3>
                  <p className="text-xs text-kb-ink-muted mt-2 line-clamp-3 flex-1">{idea.overview}</p>
                  {idea.tags?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {idea.tags.slice(0, 3).map((t) => (
                        <span key={t} className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted bg-slate-100 px-1.5 py-0.5 rounded-sm">{t}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-kb-ink-muted">
                    <span className="kb-mono uppercase tracking-wider">View details</span>
                    <ChevronRight className="w-3.5 h-3.5 text-kb-primary group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RECENTLY PUBLISHED PAPERS */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">
                RECENTLY PUBLISHED
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-kb-ink leading-tight">
                Research that <em className="italic text-kb-primary">left the lab</em>.
              </h2>
              <p className="mt-4 text-kb-ink-muted max-w-xl">
                Featured papers produced and mentored through the KhojByte ecosystem — peer-reviewed and globally indexed.
              </p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end">
              <Link to="/publications" className="kb-btn-outline" data-testid="landing-publications-cta">
                Browse all publications <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger-in">
            {papers.slice(0, 3).map((p) => <PaperCard key={p.id} paper={p} />)}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/publications" className="kb-btn-outline" data-testid="landing-publications-view-more">
              View more publications <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* STATS / NUMBERS — moved to just before footer per request */}
      {/* FINAL CTA — moved to just before footer per request */}

      {/* D.4 Mentor list */}
      <MentorHighlights />

      {/* D.5 Image carousel */}
      <ImageCarousel />

      {/* D.6 Latest news strip */}
      <LatestNewsStrip />

      {/* STATS / NUMBERS — real DB counts */}
      <section className="relative bg-gradient-to-br from-kb-subtle via-white to-kb-subtle border-y border-kb-divider overflow-hidden">
        <FloatingShapes variant="scattered" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-2 lg:grid-cols-6 gap-6">
          {[
            { k: stats?.users, l: "Researchers" },
            { k: stats?.ideas, l: "Ideas Processed" },
            { k: stats?.publications, l: "Publications" },
            { k: stats?.courses, l: "LMS Courses" },
            { k: stats?.mentors, l: "Mentors" },
            { k: stats?.universities, l: "Institutions" },
          ].map((s, i) => (
            <div key={i} data-testid={`landing-stat-${i}`}>
              <div className="kb-mono text-3xl lg:text-4xl text-kb-purple">{(s.k ?? 0).toLocaleString()}</div>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-kb-dark text-white relative overflow-hidden">
        <div aria-hidden className="absolute right-[-160px] top-[-60px] w-[520px] h-[520px] opacity-[0.18]"
          style={{ background: "radial-gradient(closest-side, #7A44FC, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-white/60 mb-4">
              <Sparkles className="inline w-3 h-3 -mt-1 mr-2" /> READY WHEN YOU ARE
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-white">
              Bring us your idea.<br />
              We'll help you take it global.
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/register?intent=submit-idea" className="bg-white text-kb-purple px-6 py-3.5 rounded-full font-semibold inline-flex items-center justify-between" data-testid="final-cta-submit-idea">
              Submit your research idea <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to="/register?intent=guidance" className="border border-white/30 text-white px-6 py-3.5 rounded-full font-semibold inline-flex items-center justify-between hover:bg-white/5" data-testid="final-cta-guidance">
              Request mentor guidance <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
